import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { X } from 'lucide-react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { mediaMin } from '../lib/breakpoints';
import { cn } from '../lib/cn';
import { IconButton } from './IconButton';

export type DrawerSide = 'right' | 'left' | 'bottom';
export type DrawerSize = 'md' | 'lg' | 'xl';

export type DrawerProps = {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  /** Extra header actions (e.g. download), left of close. */
  headerRight?: ReactNode
  /** Footer actions (Save / Cancel). */
  footer?: ReactNode
  side?: DrawerSide
  /** Width for left/right drawers. Default `md` (28rem). */
  size?: DrawerSize
  /**
   * Let the user drag the inner edge (the left edge of a right drawer) to
   * change the width; arrow keys work on the focused edge too. Left/right
   * drawers from `tablet` up only — below that the drawer stays full width.
   * `size` is the width until the user resizes.
   */
  resizable?: boolean
  /**
   * `localStorage` key for the width the user drags to, so the drawer reopens
   * at it after a reload. Without a key, the width lasts as long as the
   * Drawer stays mounted.
   */
  widthStorageKey?: string
  /** Extra classes on the panel. */
  className?: string
  /** Extra classes on the scrollable/body region. */
  bodyClassName?: string
  /** Horizontal padding on the body. Default true. */
  padded?: boolean
  /** Show close control in header. */
  showClose?: boolean
}

const sizeClass: Record<DrawerSize, string> = {
  md: 'max-w-md',
  lg: 'max-w-xl',
  xl: 'max-w-3xl',
}

const sidePanelClass: Record<DrawerSide, string> = {
  right: 'right-0 top-0 h-full w-full border-l animate-ds-drawer-right',
  left: 'left-0 top-0 h-full w-full border-r animate-ds-drawer-left',
  bottom:
    'bottom-0 left-0 right-0 max-h-[85vh] rounded-t-ds-xl border-t animate-ds-drawer-bottom',
}

const MIN_RESIZE_WIDTH = 320
/** Scrim kept beside a fully widened drawer so a click can still close it. */
const RESIZE_VIEWPORT_GUTTER = 48
const RESIZE_KEY_STEP = 16

function clampDrawerWidth(width: number): number {
  const max = Math.max(
    MIN_RESIZE_WIDTH,
    window.innerWidth - RESIZE_VIEWPORT_GUTTER,
  )
  return Math.round(Math.min(Math.max(width, MIN_RESIZE_WIDTH), max))
}

function readStoredWidth(storageKey: string | undefined): number | null {
  if (storageKey == null || typeof window === 'undefined') return null
  try {
    const width = Number(window.localStorage.getItem(storageKey))
    return Number.isFinite(width) && width >= MIN_RESIZE_WIDTH ? width : null
  } catch {
    return null
  }
}

function writeStoredWidth(storageKey: string, width: number | null) {
  try {
    if (width == null) window.localStorage.removeItem(storageKey)
    else window.localStorage.setItem(storageKey, String(width))
  } catch {
    // Storage unavailable (private window, blocked site data) — the width
    // just won't survive a reload.
  }
}

/**
 * Width of a resizable drawer: a pointer drag or arrow keys on the edge
 * handle set it, and it's saved under `storageKey` once a drag ends. `null`
 * means the user hasn't resized, so the `size` class applies.
 */
function useDrawerWidth(side: DrawerSide, storageKey: string | undefined) {
  const panelRef = useRef<HTMLDivElement>(null)
  const dragStart = useRef<{ x: number; width: number } | null>(null)
  const [width, setWidth] = useState<number | null>(() =>
    readStoredWidth(storageKey),
  )
  const [dragging, setDragging] = useState(false)
  // A right drawer grows as its left edge moves left (and vice versa).
  const direction = side === 'right' ? -1 : 1

  useEffect(() => {
    if (storageKey == null || dragging) return
    writeStoredWidth(storageKey, width)
  }, [storageKey, width, dragging])

  useEffect(() => {
    if (!dragging) return
    const onMove = (e: PointerEvent) => {
      const start = dragStart.current
      if (start == null) return
      setWidth(clampDrawerWidth(start.width + (e.clientX - start.x) * direction))
    }
    const onEnd = () => setDragging(false)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
    }
  }, [dragging, direction])

  const onHandlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const panel = panelRef.current
    if (e.button !== 0 || panel == null) return
    // Keeps the drag from selecting text in the page behind.
    e.preventDefault()
    dragStart.current = {
      x: e.clientX,
      width: panel.getBoundingClientRect().width,
    }
    setDragging(true)
  }

  const onHandleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const panel = panelRef.current
    if (panel == null) return
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const step = (e.key === 'ArrowRight' ? 1 : -1) * direction * RESIZE_KEY_STEP
    setWidth(clampDrawerWidth(panel.getBoundingClientRect().width + step))
  }

  return {
    panelRef,
    width,
    dragging,
    onHandlePointerDown,
    onHandleKeyDown,
  }
}

function DrawerResizeHandle({
  side,
  width,
  dragging,
  onPointerDown,
  onKeyDown,
}: {
  side: 'left' | 'right'
  width: number | null
  dragging: boolean
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void
  onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => void
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize drawer"
      aria-valuemin={MIN_RESIZE_WIDTH}
      aria-valuenow={width ?? undefined}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      className={cn(
        'group absolute inset-y-0 z-20 w-2 cursor-col-resize touch-none focus-visible:shadow-none',
        side === 'right' ? 'left-0 -translate-x-1/2' : 'right-0 translate-x-1/2',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 transition-colors duration-ds-fast ease-ds',
          dragging
            ? 'bg-ds-brand'
            : 'bg-transparent group-hover:bg-ds-brand group-focus-visible:bg-ds-brand',
        )}
      />
    </div>
  )
}

/**
 * Side sheet — solid surface + dialog elevation.
 * Use for related-record add, detail peek, or PDF preview.
 * Docks to `side` (default right) and slides in over a quiet scrim.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  headerRight,
  footer,
  side = 'right',
  size = 'md',
  resizable = false,
  widthStorageKey,
  className,
  bodyClassName,
  padded = true,
  showClose = true,
}: DrawerProps) {
  const resize = useDrawerWidth(side, widthStorageKey)
  const wideViewport = useMediaQuery(mediaMin('tablet'))

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  const showHeader = Boolean(title || showClose || headerRight)
  const resizeSide = resizable && wideViewport && side !== 'bottom' ? side : null
  const customWidth = resizeSide == null ? null : resize.width
  const widthClass = customWidth == null ? sizeClass[size] : 'max-w-full'

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 bg-ds-text/25"
        aria-label="Close drawer"
        onClick={onClose}
      />
      <div
        ref={resize.panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'Drawer'}
        className={cn(
          'absolute z-10 flex flex-col border-ds-border bg-ds-surface shadow-ds-dialog',
          side !== 'bottom' && widthClass,
          sidePanelClass[side],
          className,
        )}
        style={customWidth == null ? undefined : { width: customWidth }}
      >
        {resizeSide ? (
          <DrawerResizeHandle
            side={resizeSide}
            width={customWidth}
            dragging={resize.dragging}
            onPointerDown={resize.onHandlePointerDown}
            onKeyDown={resize.onHandleKeyDown}
          />
        ) : null}
        {showHeader ? (
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-ds-border px-4 py-2.5">
            <h2 className="min-w-0 truncate text-ds-md font-semibold text-ds-text">
              {title}
            </h2>
            <div className="flex shrink-0 items-center gap-0.5">
              {headerRight}
              {showClose ? (
                <IconButton label="Close" onClick={onClose}>
                  <X />
                </IconButton>
              ) : null}
            </div>
          </div>
        ) : null}
        <div
          className={cn(
            'min-h-0 flex-1 text-ds-sm text-ds-text',
            padded ? 'overflow-y-auto px-4 py-3' : 'overflow-hidden',
            bodyClassName,
          )}
        >
          {children}
        </div>
        {footer ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-ds-border px-4 py-3">
            {footer}
          </div>
        ) : null}
      </div>
      {resize.dragging ? (
        // Covers the page and any iframe (a PDF) so the drag keeps receiving
        // pointer events and the resize cursor holds outside the handle.
        <div className="absolute inset-0 z-30 cursor-col-resize" />
      ) : null}
    </div>
  )
}
